import React, { Component, ChangeEvent } from 'react';
import './notification-creator.css';
import { Form, Radio, InputProps, Input, Button, Checkbox, Message } from 'semantic-ui-react';
import { NOTIFICATIONCONFIG } from '../notifications-config';
import { NotificationModal } from '../../../modal/notification';
import { CONFIG } from '../../../helper/config';
import _ from 'lodash';

interface IProps {
    is_create_notification: boolean;
    notification?: NotificationModal;
    saveNotification: (arg: any) => void;
    teams_context: any;
}

interface IState {
    current_date: string;
    current_time: string;
    frequency: string;
    daily_frequency: string;
    daily_frequency_day_count: number;
    weekly_frequency: string[],
    monthly_frequency: string;
    monthly_frequency_day_count: number;
    monthly_frequency_month_count: number;
    submitted: boolean;
    teams_context: any;
    error_message: string;
}

/**
 * NotificationCreator component is used to create or edit a notification.
 * It will render a form with input fields to enter the notification details.
 * It will also validate the input fields and save the notification details.
 * It will use the saveNotification prop to save the notification details.
 * It will use the teams_context prop to get the Teams context.
 * It will use the notification prop to get the notification details if it is in edit mode.
 * It will use the is_create_notification prop to determine if it is in create mode or edit mode.
 * It will use the NOTIFICATIONCONFIG constant to get the input field labels and values.
 * It will use the CONFIG constant to get the URL query parameter separator.
 * It will use the semantic-ui-react library to render the form and input fields.
 * @component
 * @example
 * <NotificationCreator
 *      is_create_notification={true}
 *      saveNotification={this.saveNotification}
 *     teams_context={this.state.teams_context}
 * />
 * 
 */
export default class NotificationCreator extends Component<IProps, IState>{

    /**
     * Constructor of NotificationCreator component.
     * It will initialize the state from props notification and teams_context.
     * @param props - Properties of NotificationCreator component.
     */
    constructor(props: IProps) {
        super(props);
        let state_props: IState = this.getStateProps();
        this.state = state_props;
    }

    /**
     * Called when the component's props or state has changed.
     * If the `teams_context` prop has changed, it updates the component's state
     * with the new `teams_context` prop.
     * @param prevProps The previous props of the component.
     * @param prevState The previous state of the component.
     * @param snapshot The snapshot of the component.
     */
    componentDidUpdate(){
        if(!_.isEqual(this.props.teams_context, this.state.teams_context)){
            this.setState({teams_context: this.props.teams_context})
        }
    }

    /**
     * Returns the initial state of the component.
     * It will initialize the state with default values.
     * @returns {IState} The initial state of the component.
     */
    getStateProps = () => {
        let state_props: IState = {
            current_date: new Date().toISOString().split('T')[0],
            current_time: new Date().toTimeString().split(' ')[0].slice(0, 5),
            frequency: NOTIFICATIONCONFIG.INPUTVALUE.DAILY,
            daily_frequency: NOTIFICATIONCONFIG.INPUTVALUE.EVERY,
            daily_frequency_day_count: 1,
            weekly_frequency: Object.values(NOTIFICATIONCONFIG.INPUTVALUE.WEEKDAYS),
            monthly_frequency: NOTIFICATIONCONFIG.INPUTVALUE.MONTHLY,
            monthly_frequency_day_count: 1,
            monthly_frequency_month_count: 1,
            submitted: false,
            teams_context: this.props.teams_context,
            error_message: '',
        };
        if (!this.props.is_create_notification) {
            const { notification } = this.props;
            if (notification) {
                state_props.current_date = notification.data.date;
                state_props.current_time = notification.time;
                state_props.frequency = notification.frequency;
                if (state_props.frequency === NOTIFICATIONCONFIG.INPUTVALUE.DAILY) {
                    if (notification.data.schedule.day === NOTIFICATIONCONFIG.INPUTVALUE.EVERYWEEKDAY) {
                        state_props.daily_frequency = notification.data.schedule.day;
                    } else {
                        state_props.daily_frequency = NOTIFICATIONCONFIG.INPUTVALUE.EVERY;
                        state_props.daily_frequency_day_count = notification.data.schedule.day;
                    }
                } else if (state_props.frequency === NOTIFICATIONCONFIG.INPUTVALUE.WEEKLY) {
                    state_props.weekly_frequency = notification.data.schedule.days;
                } else if (state_props.frequency === NOTIFICATIONCONFIG.INPUTVALUE.MONTHLY) {
                    state_props.monthly_frequency_day_count = notification.data.schedule.day;
                    state_props.monthly_frequency_month_count = notification.data.schedule.month;
                    state_props.monthly_frequency = state_props.frequency;
                }
            }
        }
        return state_props;
    }

    /**
     * Handles the input change event for the form fields.
     * It updates the state with the new value of the input field.
     * @param event - The event object.
     * @param data - The data object containing the name and value of the input field.
     */
    handleInputChange = (event: ChangeEvent, data: InputProps) => {
        event.preventDefault();
        const { name, value, type } = data;
        if (type === 'checkbox') {
            let { weekly_frequency } = this.state;
            if (weekly_frequency.includes(value)) {
                weekly_frequency = weekly_frequency.filter(wf => wf !== value);
            } else {
                weekly_frequency = weekly_frequency.concat(value);
            }
            this.setState({ weekly_frequency: weekly_frequency })
        } else {
            this.setState({ [name]: value } as Pick<IState, keyof IState>);
        }
    }

    /**
     * Returns the notification information based on the current state and props.
     * It will return the notification information in the form of an object.
     * @returns {object} The notification information.
     */
    getNotificationInfo = () => {
        let { current_time, current_date, frequency } = this.state;
        let { is_create_notification, notification } = this.props;
        let res = this.getScopeAndConversationID();
        const query_params = window.location.search.split('?')[1];
        const file_name: any = query_params ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[1] : '';
        const bot_id: any = query_params ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[2] : '';
        let info;
        if (is_create_notification) {
            info = {
                time: current_time,
                date: current_date,
                frequency: frequency,
                img: file_name,
                bot_id: bot_id,
                scope: res.scope,
                conversation_id: res.conversation_id,
                destination_id: res.destination_id,
                data: this.getNotificationDataInfo(),
            };
        } else {
            info = {
                time: current_time,
                date: current_date,
                frequency: frequency,
                bot_id: bot_id,
                scope: (notification) ? notification.data.conversation_type : res.scope,
                conversation_id: (notification) ? notification.data.conversation_id : res.conversation_id,
                destination_id: res.destination_id,
                data: this.getNotificationDataInfo(),
                id:(notification)  ? notification.id : null,
            };
        }

        return info;
    }

    /**
     * Validates the form fields and checks if the notification information is valid.
     * If valid, it saves the notification information using the saveNotification prop.
     * If not valid, it sets the error message in the state.
     */
    validateForm = () => {
        let { submitted, current_time, current_date, frequency } = this.state;
        if (!submitted) {
            this.setState({ submitted: true });
        }
        let error_message = '';
        if (current_date !== '') {
            if (current_time !== '') {
                if (frequency !== '') {
                    let validation_result = this.validateFeilds();
                    if (validation_result.is_valid) {
                        let info = this.getNotificationInfo();
                        this.props.saveNotification(info);
                    } else {
                        error_message = validation_result.error_message;
                    }
                } else {
                    error_message = NOTIFICATIONCONFIG.FORMERRORMESSAGE.FREQUECYERROR;
                }
            } else {
                error_message = NOTIFICATIONCONFIG.FORMERRORMESSAGE.TIMEERROR;
            }
        } else {
            error_message = NOTIFICATIONCONFIG.FORMERRORMESSAGE.DATEERROR;
        }

        if (error_message !== '') {
            this.setState({ error_message: error_message });
        }
    }

    /**
     * Validates the form fields based on the selected frequency.
     * It checks if the daily, weekly, or monthly frequency is valid.
     * @returns {object} An object containing is_valid and error_message.
     */
    validateFeilds = () => {
        const { frequency, daily_frequency, daily_frequency_day_count, weekly_frequency,
            monthly_frequency_day_count, monthly_frequency_month_count, monthly_frequency } = this.state;
        let is_valid = false;
        let error_message = '';
        switch (frequency) {
            case NOTIFICATIONCONFIG.INPUTVALUE.DAILY:
                if (daily_frequency !== '') {
                    if (!isNaN(daily_frequency_day_count) && daily_frequency_day_count >= 1 && daily_frequency_day_count <= 31) {
                        is_valid = true;
                    } else {
                        error_message = isNaN(monthly_frequency_day_count) ? NOTIFICATIONCONFIG.FORMERRORMESSAGE.DAYERROR : NOTIFICATIONCONFIG.FORMERRORMESSAGE.DAYCOUNTERROR;
                    }
                } else {
                    error_message = NOTIFICATIONCONFIG.FORMERRORMESSAGE.DAILYFREQUENCYERROR;
                }
                break;

            case NOTIFICATIONCONFIG.INPUTVALUE.WEEKLY:
                if (weekly_frequency.length > 0) {
                    is_valid = true;
                } else {
                    error_message = NOTIFICATIONCONFIG.FORMERRORMESSAGE.WEEKERROR;
                }
                break;

            case NOTIFICATIONCONFIG.INPUTVALUE.MONTHLY:
                if (monthly_frequency !== '') {
                    if (!isNaN(monthly_frequency_day_count) && monthly_frequency_day_count >= 1 && monthly_frequency_day_count <= 31) {
                        if (!isNaN(monthly_frequency_month_count) && monthly_frequency_month_count >= 1 && monthly_frequency_month_count <= 12) {
                            is_valid = true;
                        } else {
                            error_message = isNaN(monthly_frequency_month_count) ? NOTIFICATIONCONFIG.FORMERRORMESSAGE.MONTHERROR : NOTIFICATIONCONFIG.FORMERRORMESSAGE.MONTHCONTERROR;
                        }
                    } else {
                        error_message = isNaN(monthly_frequency_day_count) ? NOTIFICATIONCONFIG.FORMERRORMESSAGE.DAYERROR : NOTIFICATIONCONFIG.FORMERRORMESSAGE.DAYCOUNTERROR;
                    }
                } else {
                    error_message = NOTIFICATIONCONFIG.FORMERRORMESSAGE.MONTHLYERROR;
                }
                break;
            default:
                break;
        }
        return { is_valid: is_valid, error_message: error_message };
    }

    /**
     * Returns the scope and conversation ID based on the current state and props.
     * It will return the scope, conversation ID, and destination ID in the form of an object.
     * @returns {object} The scope and conversation ID.
     */
    getScopeAndConversationID = () => {
        const { teams_context } = this.state;
        const query_params = window.location.search.split('?')[1];
        let scope = query_params?.split(CONFIG.URLQUERYPARAMSEPERATOR)[4] || '', conversation_id = '', destination_id = '';

        if (scope === NOTIFICATIONCONFIG.NOTIFICATIONCONVERSATIONTYPE.CHANNEL) {
            //team scope
            destination_id = teams_context.groupId;
        }
        else if (scope === NOTIFICATIONCONFIG.NOTIFICATIONCONVERSATIONTYPE.GROUP) {
            //group scope
            destination_id = teams_context.chatId;
        }
        else if (scope === NOTIFICATIONCONFIG.NOTIFICATIONCONVERSATIONTYPE.PERSONAL) {
            //personal scope
            destination_id = teams_context.userObjectId || teams_context.user_id;
         }

        conversation_id = query_params ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[3] : conversation_id;
        return { scope: scope, conversation_id: conversation_id, destination_id: destination_id }
    }

    /**
     * Returns the notification data information based on the current state.
     * It will return the notification data in the form of an object.
     * @returns {object} The notification data information.
     */
    getNotificationDataInfo = () => {
        let data = {};
        let { frequency, daily_frequency, daily_frequency_day_count, weekly_frequency,
            monthly_frequency_day_count, monthly_frequency_month_count } = this.state;
        switch (frequency) {
            case NOTIFICATIONCONFIG.INPUTVALUE.DAILY:
                data = { day: daily_frequency }
                if (daily_frequency === NOTIFICATIONCONFIG.INPUTVALUE.EVERY) {
                    data = { day: daily_frequency_day_count }
                }
                break;

            case NOTIFICATIONCONFIG.INPUTVALUE.WEEKLY:
                data = { days: weekly_frequency };
                break;

            case NOTIFICATIONCONFIG.INPUTVALUE.MONTHLY:
                data = {
                    day: monthly_frequency_day_count,
                    month: monthly_frequency_month_count
                }
                break;

            default:
                break;
        }
        return data;
    }

    /**
     * Dismisses the error message in the notification form.
     * Clears the `error_message` state when the user closes the error banner.
     */
    handleDismiss = () => {
        this.setState({ error_message: '' });
    }

    /**
     * Renders the notification creator form.
     * It will render the form with the input fields and buttons.
     * @returns {JSX.Element} The notification creator form.
     */
    render() {
        let { current_date, current_time, frequency, daily_frequency, daily_frequency_day_count, weekly_frequency,
            monthly_frequency, monthly_frequency_day_count, monthly_frequency_month_count, submitted, error_message } = this.state;
        let week_days_label = Object.values(NOTIFICATIONCONFIG.INPUTLABEL.WEEKDAYS);
        let week_days_values = Object.values(NOTIFICATIONCONFIG.INPUTVALUE.WEEKDAYS);
        return (
            <div id='notification'>

                <Form id='create-notification-form' onSubmit={this.validateForm} error={submitted && error_message !== ''} >
                    <Message onDismiss={this.handleDismiss} style={{ marginBottom: '10px' }}
                        content={error_message} attached='top' floating error />
                    <Form.Group inline>
                        <Form.Field
                            control={Input}
                            label={NOTIFICATIONCONFIG.INPUTLABEL.DATE}
                            type='date'
                            value={current_date}
                            name='current_date'
                            onChange={this.handleInputChange}
                        />
                        <Form.Field
                            control={Input}
                            label={NOTIFICATIONCONFIG.INPUTLABEL.TIME}
                            type='time'
                            value={current_time}
                            name='current_time'
                            onChange={this.handleInputChange}
                        />
                    </Form.Group>
                    <div id='container'>
                        <div>
                            <Form.Field
                                control={Radio}
                                label={NOTIFICATIONCONFIG.INPUTLABEL.DAILY}
                                value={NOTIFICATIONCONFIG.INPUTVALUE.DAILY}
                                checked={frequency === NOTIFICATIONCONFIG.INPUTVALUE.DAILY}
                                name='frequency'
                                onChange={this.handleInputChange}
                            />
                            <Form.Field
                                control={Radio}
                                label={NOTIFICATIONCONFIG.INPUTLABEL.WEEKLY}
                                value={NOTIFICATIONCONFIG.INPUTVALUE.WEEKLY}
                                checked={frequency === NOTIFICATIONCONFIG.INPUTVALUE.WEEKLY}
                                name='frequency'
                                onChange={this.handleInputChange}
                            />

                            <Form.Field
                                control={Radio}
                                label={NOTIFICATIONCONFIG.INPUTLABEL.MONTHLY}
                                value={NOTIFICATIONCONFIG.INPUTVALUE.MONTHLY}
                                checked={frequency === NOTIFICATIONCONFIG.INPUTVALUE.MONTHLY}
                                name='frequency'
                                onChange={this.handleInputChange}
                            />
                        </div>
                        <div id='panel'>
                            <div id='content-div'>
                                <div className='selected daily'
                                    style={{ display: (frequency === NOTIFICATIONCONFIG.INPUTVALUE.DAILY) ? 'block' : 'none' }}>
                                    <Form.Group inline>
                                        <Form.Field
                                            control={Radio}
                                            label={NOTIFICATIONCONFIG.INPUTLABEL.EVERY}
                                            value={NOTIFICATIONCONFIG.INPUTVALUE.EVERY}
                                            checked={daily_frequency === NOTIFICATIONCONFIG.INPUTVALUE.EVERY}
                                            name='daily_frequency'
                                            onChange={this.handleInputChange}
                                        />
                                        <Form.Field
                                            control={Input} type='number'
                                            value={daily_frequency_day_count}
                                            name='daily_frequency_day_count'
                                            onChange={this.handleInputChange}
                                        />
                                        <label htmlFor='days'>{NOTIFICATIONCONFIG.INPUTLABEL.DAILY_DAYS}</label>
                                    </Form.Group>
                                    <Form.Field
                                        control={Radio}
                                        label={NOTIFICATIONCONFIG.INPUTLABEL.EVERYWEEKDAY}
                                        value={NOTIFICATIONCONFIG.INPUTVALUE.EVERYWEEKDAY}
                                        checked={daily_frequency === NOTIFICATIONCONFIG.INPUTVALUE.EVERYWEEKDAY}
                                        name='daily_frequency'
                                        onChange={this.handleInputChange}
                                    />

                                </div>
                                <div className='selected weekly'
                                    style={{ display: (frequency === NOTIFICATIONCONFIG.INPUTVALUE.WEEKLY) ? 'block' : 'none' }}>

                                    {/* <Form.Group inline> */}
                                    {
                                        week_days_label.map((wd, i) => {
                                            return <Form.Field key={wd}
                                                control={Checkbox}
                                                label={wd}
                                                value={week_days_values[i]}
                                                checked={weekly_frequency.includes(week_days_values[i])}
                                                name='weekly_frequency'
                                                onChange={this.handleInputChange}
                                            />
                                        })
                                    }

                                    {/* </Form.Group> */}
                                </div>
                                <div className='selected monthly'
                                    style={{ display: (frequency === NOTIFICATIONCONFIG.INPUTVALUE.MONTHLY) ? 'block' : 'none' }}>
                                    <Form.Group inline>
                                        <Form.Field
                                            control={Radio}
                                            label={NOTIFICATIONCONFIG.INPUTLABEL.DAY}
                                            value={NOTIFICATIONCONFIG.INPUTVALUE.MONTHLY}
                                            checked={monthly_frequency === NOTIFICATIONCONFIG.INPUTVALUE.MONTHLY}
                                            name='monthly_frequency'
                                            onChange={this.handleInputChange}
                                        />
                                        <Form.Field
                                            control={Input} type='number'
                                            value={monthly_frequency_day_count}
                                            name='monthly_frequency_day_count'
                                            onChange={this.handleInputChange}
                                        />
                                        <label htmlFor='days'>{NOTIFICATIONCONFIG.INPUTLABEL.OFEVERY}</label>
                                        <Form.Field
                                            control={Input} type='number'
                                            value={monthly_frequency_month_count}
                                            name='monthly_frequency_month_count'
                                            onChange={this.handleInputChange}
                                        />
                                        <label htmlFor='days'>{NOTIFICATIONCONFIG.INPUTLABEL.MONTHS}</label>
                                    </Form.Group>
                                </div>

                            </div>
                            <Button className='btn button-primary' type='submitted'>Save</Button>
                        </div>
                    </div>
                </Form>
            </div>
        )
    }
}